import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import AxiosInstance from "../utils/Axios.ts";
import DynamicTab from "../components/extras/DynamicTab.tsx";
import LoadingSpinner from "../components/basics/LoadingSpinner.tsx";
import ErrorMessage from "../components/basics/ErrorMessage.tsx";

interface DokkChangeRow {
  dokk_id?: number | null;
  vessel_imo?: number | null;
  vessel_name?: string | null;
  port_name?: string | null;
  change_type?: string | null;
  change_label?: string | null;
  severity?: string | null;
  old_value?: string | null;
  new_value?: string | null;
  location_id_old?: number | null;
  location_id_new?: number | null;
  portcall_arrival?: string | null;
  ctp_booking_id?: number | null;
  change_hash?: string | null;
  alerted_at?: string | null;
  detected_at?: string | null;
}

interface DashboardResponse {
  last_checked_at?: string;
  changes?: DokkChangeRow[];
  error?: string;
  detail?: string;
}

/** OR: Critical severity OR dock/port-related change types */
function buildDefaultFilterModel(): GridFilterModel {
  return {
    logicOperator: GridLogicOperator.Or,
    items: [
      { id: 1, field: "severity", operator: "equals", value: "CRITICAL" },
      { id: 2, field: "change_type", operator: "equals", value: "dock_change" },
      { id: 3, field: "change_type", operator: "equals", value: "port_change" },
      { id: 4, field: "change_type", operator: "startsWith", value: "dock_mismatch" },
    ],
  };
}

const EMPTY_FILTER_MODEL: GridFilterModel = { items: [] };

function formatDateTime(value: string | null | undefined): string {
  if (value == null || value === "") {
    return "—";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function severityChipColor(
  severity: string | null | undefined
): "error" | "warning" | "info" | "default" {
  const s = (severity || "").toUpperCase();
  if (s === "CRITICAL") {
    return "error";
  }
  if (s === "HIGH") {
    return "warning";
  }
  if (s === "MEDIUM" || s === "LOW") {
    return "info";
  }
  return "default";
}

const dokkMonitorColumns = (): GridColDef[] => [
  {
    field: "detected_at",
    headerName: "Detected",
    width: 168,
    minWidth: 140,
    filterable: true,
    sortable: true,
    valueFormatter: (value) =>
      formatDateTime(typeof value === "string" ? value : value ?? undefined),
  },
  {
    field: "alerted_at",
    headerName: "Alerted",
    width: 168,
    minWidth: 140,
    filterable: true,
    sortable: true,
    valueFormatter: (value) =>
      formatDateTime(typeof value === "string" ? value : value ?? undefined),
  },
  {
    field: "dokk_id",
    headerName: "Dokk ID",
    width: 100,
    minWidth: 88,
    type: "number",
    filterable: true,
    sortable: true,
    valueGetter: (_value, row: DokkGridRow) => row.dokk_id ?? undefined,
  },
  {
    field: "vesselCell",
    headerName: "Vessel",
    flex: 1,
    minWidth: 160,
    filterable: true,
    sortable: true,
    sortComparator: (v1, v2) =>
      String(v1 ?? "").localeCompare(String(v2 ?? ""), undefined, {
        sensitivity: "base",
      }),
    valueGetter: (_value, row: DokkGridRow) =>
      [row.vessel_name, row.vessel_imo != null ? `IMO ${row.vessel_imo}` : ""]
        .filter(Boolean)
        .join(" "),
    renderCell: (params) => (
      <Box sx={{ py: 0.5 }}>
        <Typography variant="body2">{params.row.vessel_name || "—"}</Typography>
        {params.row.vessel_imo != null ? (
          <Typography variant="caption" display="block" color="text.secondary">
            IMO {params.row.vessel_imo}
          </Typography>
        ) : null}
      </Box>
    ),
  },
  {
    field: "port_name",
    headerName: "Port",
    width: 140,
    minWidth: 120,
    filterable: true,
    sortable: true,
    valueFormatter: (v) => (v ? String(v) : "—"),
  },
  {
    field: "portcall_arrival",
    headerName: "Portcall arrival",
    width: 168,
    minWidth: 140,
    filterable: true,
    sortable: true,
    valueGetter: (_value, row: DokkGridRow) => row.portcall_arrival ?? undefined,
    valueFormatter: (value) =>
      formatDateTime(typeof value === "string" ? value : value ?? undefined),
  },
  {
    field: "change_label",
    headerName: "Change (label)",
    width: 200,
    minWidth: 160,
    flex: 1,
    filterable: true,
    sortable: true,
    valueFormatter: (v) => (v ? String(v) : "—"),
  },
  {
    field: "change_type",
    headerName: "Change (raw)",
    width: 200,
    minWidth: 140,
    filterable: true,
    sortable: true,
    valueFormatter: (v) => (v ? String(v) : "—"),
  },
  {
    field: "severity",
    headerName: "Severity",
    width: 120,
    minWidth: 108,
    filterable: true,
    sortable: true,
    renderCell: (params) => {
      const sev = params.row.severity as string | null | undefined;
      return sev ? (
        <Chip size="small" label={sev} color={severityChipColor(sev)} variant="outlined" sx={{ mt: 0.5 }} />
      ) : (
        "—"
      );
    },
  },
  {
    field: "old_value",
    headerName: "Old",
    width: 160,
    minWidth: 120,
    filterable: true,
    sortable: true,
    flex: 0.6,
    valueFormatter: (v) => (v != null && v !== "" ? String(v) : "—"),
  },
  {
    field: "new_value",
    headerName: "New",
    width: 160,
    minWidth: 120,
    filterable: true,
    sortable: true,
    flex: 0.6,
    valueFormatter: (v) => (v != null && v !== "" ? String(v) : "—"),
  },
  {
    field: "location_ids",
    headerName: "Loc IDs",
    width: 140,
    minWidth: 120,
    filterable: true,
    sortable: true,
    valueGetter: (_value, row: DokkGridRow) =>
      `${row.location_id_old ?? "—"} → ${row.location_id_new ?? "—"}`,
  },
  {
    field: "ctp_booking_id",
    headerName: "CTP booking",
    width: 120,
    minWidth: 100,
    type: "number",
    filterable: true,
    sortable: true,
    valueGetter: (_value, row: DokkGridRow) => row.ctp_booking_id ?? undefined,
  },
];

type DokkGridRow = DokkChangeRow & { id: string };

const DokkMonitorPage: React.FC = () => {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => EMPTY_FILTER_MODEL);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await AxiosInstance.get<DashboardResponse>("/dokk-monitor/dashboard/", {
        params: { limit: 200 },
      });
      if (response.status === 200) {
        setData(response.data);
        if (response.data?.error && !response.data?.changes) {
          setError(response.data.error);
        }
      }
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { error?: string; detail?: string }; status?: number };
      };
      const msg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        (e instanceof Error ? e.message : "Failed to load Dokk Monitor data.");
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const columns = useMemo(() => dokkMonitorColumns(), []);

  const rows: DokkGridRow[] = useMemo(() => {
    const changes = data?.changes ?? [];
    return changes.map((row, idx) => ({
      ...row,
      id: row.change_hash ? `${row.change_hash}-${idx}` : `row-${idx}`,
    }));
  }, [data?.changes]);

  const applyDefaultView = useCallback(() => {
    setFilterModel(buildDefaultFilterModel());
  }, []);

  const showAll = useCallback(() => {
    setFilterModel(EMPTY_FILTER_MODEL);
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  const changes = data?.changes ?? [];
  const listError = error || (data?.error && !changes.length ? data.error : null);

  if (listError && !changes.length) {
    return (
      <DynamicTab title="Dokk Monitor | IT Master" favicon="/logos/IT logo.png">
        <ErrorMessage message={listError} />
      </DynamicTab>
    );
  }

  return (
    <DynamicTab title="Dokk Monitor | IT Master" favicon="/logos/IT logo.png">
      <Box sx={{ mx: "auto", width: "100%", p: { xs: 1, sm: 2 } }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" component="h1" gutterBottom>
            Dokk Monitor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last change-feed check (UTC reference):{" "}
            <strong>{formatDateTime(data?.last_checked_at)}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Data is loaded from the Dokk Monitor service. Use <strong>Default view</strong> to limit the
            grid to critical / dock-port-related rows; filtering and sorting are applied in your browser only.
            Use the             grid toolbar <strong>Columns</strong> control to show optional fields (for example Alerted,
            Dokk ID, Change raw, Loc IDs).
          </Typography>
        </Paper>

        {listError && changes.length > 0 ? (
          <Typography color="warning.main" variant="body2" sx={{ mb: 1 }}>
            {listError}
          </Typography>
        ) : null}

        <Paper sx={{ width: "100%", height: 640 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            filterMode="client"
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 50, page: 0 } },
              sorting: {
                sortModel: [{ field: "detected_at", sort: "desc" }],
              },
              columns: {
                columnVisibilityModel: {
                  alerted_at: false,
                  dokk_id: false,
                  change_type: false,
                  location_ids: false,
                },
              },
            }}
            slots={{
              toolbar: () => (
                <GridToolbarContainer sx={{ gap: 1, p: 1, flexWrap: "wrap", alignItems: "center" }}>
                  <Button size="small" variant="outlined" onClick={applyDefaultView}>
                    Default view
                  </Button>
                  <Button size="small" variant="outlined" onClick={showAll}>
                    Show all
                  </Button>
                  <GridToolbarFilterButton />
                  <GridToolbarDensitySelector />
                  <GridToolbarColumnsButton />
                  <Box sx={{ flex: 1 }} />
                  <GridToolbarQuickFilter quickFilterProps={{ debounceMs: 400 }} />
                </GridToolbarContainer>
              ),
            }}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": { bgcolor: "background.default" },
              "& .MuiDataGrid-cell": { alignItems: "flex-start" },
            }}
          />
        </Paper>

        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
            No change records in the log yet.
          </Typography>
        ) : null}
      </Box>
    </DynamicTab>
  );
};

export default DokkMonitorPage;
